import { APIFetchError } from "@cur8/api-client";
import { fromAPI, ScheduleViewSlot } from "@cur8/rich-entity";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { APIClient } from "lib/api/client";
import {
  FailureKind,
  fetchInBisectedWindows,
  TimeWindow,
} from "lib/fetchInBisectedWindows";
import { fetchPages } from "lib/fetchPages";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useAPIClient } from "render/context/APIContext";

export interface BookedSlot {
  // Names the booking rather than the member, so a slot that outlived its
  // member can still be reported to whoever can unassign it.
  slotId: string;
  // Both absent when the slot outlived the member it was booked for: the
  // schedule still calls it booked, but there is no longer a visit or a member
  // behind it. Callers must not query for those.
  visitId: string | undefined;
  patientId: string | undefined;
}

export interface TodaysSchedule {
  bookedSlots: BookedSlot[];
  failedWindows: TimeWindow[];
  // Nothing could be read for the whole day, as opposed to gaps within it.
  failedEntirely: boolean;
  // What refused the failed windows. The schedule endpoint hard-failing a whole
  // range on one unservable record is a server-side fault, and this is what
  // makes it attributable as one rather than just a hole in the day.
  failureReasons: string[];
}

// Bookings move on the timescale of somebody making one, and the operator keeps
// this screen open all day, so navigating back to it should not re-read the day.
const STALE_MS = 60_000;
// Long enough to outlive an operator working elsewhere in the app. On the
// default 5 minutes the day is dropped and a poisoned one is bisected from
// scratch, which is a 15 second wait rather than a background refresh.
const KEEP_MS = 30 * 60_000;

export function useTodaysScheduleQuery({
  siteId,
}: {
  siteId: string | undefined;
}) {
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();

  const today = useMemo<TimeWindow>(() => {
    const now = DateTime.now();
    return {
      start: now.startOf("day"),
      end: now.plus({ days: 1 }).startOf("day"),
    };
  }, []);

  return useQuery({
    queryKey: queryKey(siteId),
    queryFn: siteId
      ? queryFn(
          apiClient,
          siteId,
          today,
          () =>
            queryClient.getQueryData<TodaysSchedule>(queryKey(siteId))
              ?.failedWindows
        )
      : skipToken,
    staleTime: STALE_MS,
    gcTime: KEEP_MS,
  });
}

function queryFn(
  apiClient: APIClient,
  siteId: string,
  today: TimeWindow,
  lastFailedWindows: () => TimeWindow[] | undefined
) {
  return async function (): Promise<TodaysSchedule> {
    const { items, failedWindows, failedEntirely, failureReasons } =
      await fetchInBisectedWindows({
        window: today,
        fetchWindow: (window) => fetchScheduleWindow(apiClient, siteId, window),
        knownFailedWindows: lastFailedWindows(),
        classifyFailure: classifyScheduleFailure,
      });

    // Keyed by slot id because a slot starting exactly on a bisect boundary can
    // come back from both of the windows that meet there.
    const bookedSlots = new Map<string, BookedSlot>();
    for (const slot of items) {
      if (slot?.isBooked !== true) {
        continue;
      }
      bookedSlots.set(slot.id, {
        slotId: slot.id,
        visitId: slot.visitInfo?.visitId,
        patientId: slot.patientId,
      });
    }

    return {
      bookedSlots: [...bookedSlots.values()],
      failedWindows,
      failedEntirely,
      failureReasons,
    };
  };
}

// Only a 5xx can be about the range: the endpoint fails a whole window on one
// unservable record, and a window that leaves the record out can still be
// served. A 401 or an iPad with no network fails identically however small the
// range, so it is reported after one request instead of six more.
function classifyScheduleFailure(error: unknown): FailureKind {
  if (error instanceof APIFetchError) {
    const { status } = error.response;
    return { rangeDependent: status >= 500, reason: `http-${status}` };
  }

  return {
    rangeDependent: false,
    reason: error instanceof Error ? error.name : "unknown",
  };
}

async function fetchScheduleWindow(
  apiClient: APIClient,
  siteId: string,
  window: TimeWindow
): Promise<ScheduleViewSlot[]> {
  const start = window.start.toISO();
  const end = window.end.toISO();
  if (start == null || end == null) {
    throw new Error("Invalid schedule window");
  }

  const slots: ScheduleViewSlot[] = [];

  // The endpoint caps a page well below a busy site's day, so a window that
  // stops at the first page silently loses its later bookings.
  const pages = fetchPages(
    (continuationToken) =>
      apiClient.booking.getScheduleViewForSite(
        { siteId },
        { start, end, continuationToken }
      ).result
  );

  for await (const rooms of pages) {
    for (const room of rooms) {
      const roomSlots = fromAPI.toRoomWithScheduleViewSlot(room).slots;
      if (roomSlots != null) {
        slots.push(...roomSlots);
      }
    }
  }

  return slots;
}

function queryKey(siteId: string | undefined) {
  return ["schedule", siteId] as const;
}
